import axiosInstance from './axiosInstance';

const API_URL = `${process.env.REACT_APP_API_URL}/api/tasks`;

export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH';
export type TaskStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
export type AssigneeStatus = 'PENDING' | 'COMPLETED';
export type TaskNotificationType =
  | 'TASK_ASSIGNED'
  | 'TASK_UPDATED'
  | 'TASK_REMINDER'
  | 'ATTENDANCE_REQUEST_UPDATE'
  | 'HOMEWORK_ASSIGNED';

export interface AssigneeInput {
  teacherId: number;
  teacherName: string;
  subjectName: string;
  subjectCode: string;
}

export interface TaskCreatePayload {
  title: string;
  description: string;
  schoolId: number;
  classId: number;
  className: string;
  startDate: string;
  endDate: string;
  priority: TaskPriority;
  assignees: AssigneeInput[];
}

export interface TaskUpdatePayload {
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  status?: TaskStatus;
}

export interface AssigneeResponse {
  id: number;
  teacherId: number;
  teacherName: string;
  subjectName: string;
  isRead: boolean;
  assigneeStatus: AssigneeStatus;
  completionNote?: string;
  late: boolean;
}

export interface TaskResponse {
  id: number;
  title: string;
  description: string;
  classId: number;
  className: string;
  startDate: string;
  endDate: string;
  status: TaskStatus;
  priority: TaskPriority;
  assignedByName: string;
  createdAt: string;
  assigneeCount: number;
  assignees?: AssigneeResponse[];
}

export interface NotificationItem {
  id: number;
  message: string;
  isRead: boolean;
  createdAt: string;
  notificationType: TaskNotificationType;
  // For notificationType "HOMEWORK_ASSIGNED" this field actually holds the homework id, not a task id.
  taskId: number;
}

export interface PaginatedNotifications {
  content: NotificationItem[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface UnreadCountResponse {
  count: number;
}

export const createTask = (data: TaskCreatePayload) =>
  axiosInstance.post<TaskResponse>(API_URL, data);

export const getTasksBySchool = (schoolId: number) =>
  axiosInstance.get<TaskResponse[]>(`${API_URL}/school/${schoolId}`);

export const getTasksByClass = (schoolId: number, classId: number) =>
  axiosInstance.get<TaskResponse[]>(`${API_URL}/school/${schoolId}/class/${classId}`);

export const getTaskById = (taskId: number) =>
  axiosInstance.get<TaskResponse>(`${API_URL}/${taskId}`);

export const updateTask = (taskId: number, data: TaskUpdatePayload) =>
  axiosInstance.put<TaskResponse>(`${API_URL}/${taskId}`, data);

export const deleteTask = (taskId: number) =>
  axiosInstance.delete(`${API_URL}/${taskId}`);

export const getTasksForTeacher = (teacherId: number) =>
  axiosInstance.get<TaskResponse[]>(`${API_URL}/teacher/${teacherId}`);

export const markTaskRead = (assigneeId: number, teacherId: number) =>
  axiosInstance.put(`${API_URL}/assignee/${assigneeId}/read`, null, {
    params: { teacherId },
  });

export const markTaskComplete = (assigneeId: number, note: string) =>
  axiosInstance.put<AssigneeResponse>(`${API_URL}/assignee/${assigneeId}/complete`, { completionNote: note });

export const getNotifications = (userId: number, page = 0, size = 10) =>
  axiosInstance.get<PaginatedNotifications>(`${API_URL}/notifications/user/${userId}`, {
    params: { page, size, sort: 'createdAt,desc' },
  });

export const getUnreadCount = (userId: number) =>
  axiosInstance.get<UnreadCountResponse>(`${API_URL}/notifications/user/${userId}/unread-count`);

export const markNotificationRead = (notifId: number) =>
  axiosInstance.put<NotificationItem>(`${API_URL}/notifications/${notifId}/read`);

export const markAllNotificationsRead = (userId: number) =>
  axiosInstance.put(`${API_URL}/notifications/user/${userId}/read-all`);
